import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import {
  toWidget,
  viewToModelPositionOutsideModelElement
} from '@ckeditor/ckeditor5-widget/src/utils';
import Widget from '@ckeditor/ckeditor5-widget/src/widget';
import { CkeditorConstants } from '../../src/utils';

const { htmlElement: {
  IMAGE, AUDIO, VIDEO, SOURCE, PARAGRAPH, SPAN
}, htmlAttributes: { ID, TITLE, ABOUT },
  helperAttributes: { DATA_NODE_NAME, DATA_DESCRIPTION, DATA_CREDIT_LINE, DATA_TENANT_ID },
  event: { MEDIA_SELECTED }
} = CkeditorConstants;

export default class FigureEditing extends Plugin {
  static get requires() {
    return [Widget];
  }

  init() {
    this._defineSchema();
    this._defineConverters();
    this.editor.editing.mapper.on(
      'viewToModelPosition',
      viewToModelPositionOutsideModelElement(this.editor.model, viewElement => viewElement.hasClass('media-class'))
    );

    this.listenTo(this.editor.editing.view.document, 'click', evt => {
      const selectedElement = this.editor.model.document.selection.getSelectedElement();
      if (selectedElement) {
        if (selectedElement?.name === 'figureTag' || selectedElement?.name === 'inlineimage') {
            const childNode = selectedElement?._children?._nodes?.[0];
          const subChildNode = childNode?._children?._nodes?.[0];
          if (childNode?.name === AUDIO && subChildNode?.name === SOURCE) {
            const audioObj = {
              mediaName: AUDIO,
              'name': subChildNode?._attrs.get(DATA_NODE_NAME),
              'mediaId': childNode?._attrs.get(ID),
              'title': subChildNode?._attrs.get(TITLE),
              'desc': subChildNode?._attrs.get(DATA_DESCRIPTION),
              'credLine': subChildNode?._attrs.get(DATA_CREDIT_LINE),
              'dataTenantId': subChildNode?._attrs.get(DATA_TENANT_ID)
            }
            this.editor.fire(MEDIA_SELECTED, audioObj);
          } else if (childNode?.name === SPAN && subChildNode?.name === VIDEO) {
            const superChildNode = subChildNode?._children?._nodes?.[0];
            const videoObj = {
              mediaName: VIDEO,
              'name': superChildNode?._attrs.get(DATA_NODE_NAME),
              'mediaId': subChildNode?._attrs.get(ID),
              'title': superChildNode?._attrs.get(TITLE),
              'desc': superChildNode?._attrs.get(DATA_DESCRIPTION),
              'credLine': superChildNode?._attrs.get(DATA_CREDIT_LINE),
              'dataTenantId': superChildNode?._attrs.get(DATA_TENANT_ID)
            }
            this.editor.fire(MEDIA_SELECTED, videoObj);
          } else if (childNode?.name === SPAN && subChildNode?.name === IMAGE) {
            const imgObj = {
              mediaName: 'image',
              'name': subChildNode?._attrs.get(DATA_NODE_NAME),
              'imageId': subChildNode?._attrs.get(ID),
              'height': subChildNode?._attrs.get("height"),
              'width': subChildNode?._attrs.get("width"),
              'align': subChildNode?.parent?.parent?.getAttribute('style')?.split(':')[1].replace(/;/g, "") || '',
              'altText':  subChildNode?._attrs.get("alt") || subChildNode?._attrs.get(DATA_NODE_NAME),
              'imageType': 'block',
              'dataTenantId': subChildNode?._attrs.get(DATA_TENANT_ID)
            }
            this.editor.fire(MEDIA_SELECTED, imgObj);
          }
        }

        if (selectedElement?.name === "table") {
          this.editor.fire(MEDIA_SELECTED, { mediaName: null });
        }
      }
    });
  }

  _defineSchema() {
    const schema = this.editor.model.schema;
    schema.register('figureTag', {
      allowWhere: ['$text', '$block'],
      allowContentOf: '$root',
      isInline: false,
      isObject: true,
      allowAttributesOf: '$text',
      inheritAllFrom: PARAGRAPH,
      allowAttributes: ['class', 'style', 'data-cke-ignore-events','alignment', ID, ABOUT]
    });
  }

  _defineConverters() {
    const conversion = this.editor.conversion;
    this.editor.model.schema.addAttributeCheck(context => {
      if (context.endsWith('figureTag')) {
        return true;
      }
    });

    conversion.for('upcast').elementToElement({
      view: {
        name: 'figure',
        classes: 'media-class'
      },
      model: (viewElement, { writer: modelWriter }) => {
        return modelWriter.createElement('figureTag', viewElement.getAttributes());
      }
    });

    conversion.for('dataDowncast').elementToElement({
      model: 'figureTag',
      view: (modelItem, { writer: viewWriter }) => {
        const figure = viewWriter.createContainerElement('figure', { class: 'media-class', ...Object.fromEntries(modelItem.getAttributes()) });
        return figure;
      }
    });

    conversion.for('editingDowncast').elementToElement({
      model: 'figureTag',
      view: (modelItem, { writer: viewWriter }) => {
        const figure = viewWriter.createContainerElement('figure', { class: 'media-class', ...Object.fromEntries(modelItem.getAttributes()) });
        return toWidget(figure, viewWriter, { hasSelectionHandle: true });
      }
    });

    conversion.for('downcast')
      .add(dispatcher => {
        dispatcher.on('attribute', (evt, data, conversionApi) => {
          if ((data.item.name === 'figureTag')) {
            const viewWriter = conversionApi.writer;
            const viewElement = conversionApi.mapper.toViewElement(data.item);
            if (Array.isArray(data?.attributeNewValue) && data.attributeNewValue?.includes('text-align:')) {
              viewWriter.setAttribute(data.attributeKey, data.attributeNewValue, viewElement);
            }
          }
        });
      });
  }
}
